import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { toast } from "sonner";

import { BRAND } from "~/brand";
import { LoadingScreen } from "~/components/LoadingScreen";
import { ActiveCharger } from "~/components/dashboard/ActiveCharger";
import { ChargingSessions } from "~/components/dashboard/ChargingSessions";
import { ChargingSessionsTable } from "~/components/dashboard/ChargingSessionsTable";
import { DashboardStats } from "~/components/dashboard/DashboardStats";
import { PaymentList } from "~/components/dashboard/PaymentList";
import { TaskList } from "~/components/dashboard/TaskList";
import { VolumeTierCard } from "~/components/dashboard/VolumeTierCard";
import {
  chargerDocumentsListQueryOptions,
  contractTypesQueryOptions,
  currentContractQueryOptions,
  ensureDefaultSessionYear,
  profileQueryOptions,
  sessionsMonthlyListQueryOptions,
  sessionUploadLimitQueryOptions,
} from "~/lib/api";

export const Route = createFileRoute("/(authenticated)/_app/dashboard/")({
  loader: async ({ context }) => {
    // Not awaited: the sessions table has its own Suspense boundary, so the dashboard need
    // not wait for it -- this only starts the two requests before the table mounts. A
    // failure surfaces in the table's own query.
    void ensureDefaultSessionYear(context.queryClient).catch(() => undefined);
    context.queryClient.invalidateQueries(profileQueryOptions());
    context.queryClient.invalidateQueries(currentContractQueryOptions());
    await Promise.all([
      context.queryClient.refetchQueries({
        queryKey: chargerDocumentsListQueryOptions("sessions").queryKey,
        type: "all",
      }),
      context.queryClient.refetchQueries({
        queryKey: sessionUploadLimitQueryOptions().queryKey,
        type: "all",
      }),
      context.queryClient.ensureQueryData(contractTypesQueryOptions()),
    ]);
    return context.queryClient.ensureQueryData(profileQueryOptions());
  },
  pendingComponent: LoadingScreen,
  component: DashboardIndex,
});

function DashboardIndex() {
  const location = useLocation();
  const navigate = useNavigate();
  const reconnected =
    new URLSearchParams(location.searchStr).get("reconnected") ?? undefined;
  useSuspenseQuery(sessionsMonthlyListQueryOptions());

  useEffect(() => {
    if (reconnected === "true") {
      toast.success("Account opnieuw verbonden");
      navigate({ to: "/dashboard", replace: true });
    }
  }, [reconnected, navigate]);

  return (
    <div className="flex min-h-dvh flex-col gap-4 px-4 py-6 lg:px-12 lg:py-10">
      {/* Mobile-only: TaskList above stats */}
      <div className="lg:hidden">
        <Suspense>
          <TaskList collapsible />
        </Suspense>
      </div>

      <div className="flex flex-1 flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(320px,1fr)] lg:gap-6">
        <div className="flex flex-col">
          <div className="mb-4 flex flex-col gap-4">
            <DashboardStats />
            {BRAND.volumeBenefit && (
              <Suspense>
                <VolumeTierCard className="lg:hidden" />
              </Suspense>
            )}
          </div>
          <Suspense>
            <ChargingSessions />
            <PaymentList />
            <ChargingSessionsTable />
          </Suspense>
        </div>

        {/* Desktop: volume tier card, active charger + TaskList in sidebar */}
        <div className="hidden lg:block">
          <Suspense>
            <div className="flex flex-col gap-4">
              {BRAND.volumeBenefit && <VolumeTierCard />}
              <ActiveCharger />
              <TaskList />
            </div>
          </Suspense>
        </div>
      </div>
    </div>
  );
}
